import { FieldProps, FunctionField, useGetOne, useRecordContext } from "react-admin";
import { AutovioCalendarEvent } from "../model/autovioCalendarEvents";
import * as React from "react";
import { type ReactNode } from "react";
import Typography from "@mui/material/Typography";
import { sanitizeFieldRestProps } from "ra-ui-materialui/src/field/sanitizeFieldRestProps";
import { Tooltip } from "@mui/material";
import { User } from "../providers/usersProvider";

/**
 * Works on `AutovioCalendarEvent` records.
 */
export function CalendarEventStatusField(
  props: Omit<FieldProps, "source"> & {
    component?: string;
    color?: string;
    variant?: string;
    transform?: (displayedStatus: string) => string;
    tooltip?: boolean;
  },
): ReactNode {
  const { className, component = "div", variant = "body2", transform = (s) => s, tooltip = true, ...rest } = props;
  const calendarEvent = useRecordContext(props) as undefined | AutovioCalendarEvent;
  const { data: deletedByUser } = useGetOne<User>(
    "users",
    { id: calendarEvent?.deletedBy || "" },
    { enabled: !!calendarEvent?.deletedBy },
  );

  if (!calendarEvent?.status) {
    return "";
  }

  const status = (
    <Typography component={component} variant={variant} className={className} {...sanitizeFieldRestProps(rest)}>
      {transform(
        (() => {
          switch (calendarEvent.status) {
            case "invited":
              return "⏳️ noch nicht bestätigt";
            case "booked":
              return calendarEvent.createdAt > calendarEvent.end ? "✔ bestätigt" : "✔ gebucht";
            case "declinedByStudent":
              return calendarEvent.createdAt > calendarEvent.end ? "❌ nicht bestätigt" : "❌ Einladung abgelehnt";
            case "canceledByStudent":
              return "❌ abgesagt vom Fahrschüler";
            case "canceledByInstructor": {
              let status = calendarEvent.type === "Other" ? "❌ gelöscht" : "❌ abgesagt";
              if (deletedByUser) {
                status += ` von ${deletedByUser.name}`;
                if (deletedByUser.isAutovioStaff) {
                  status += " (AUTOVIO)";
                }
              }
              return status;
            }
            case "finished":
              return "🏁 beendet";
            case "passed":
              return "👍 bestanden";
            case "failed":
              return "👎 durchgefallen";
            case "no-show":
              return "❌ nicht erschienen";
            default:
              console.error("Could not render status of calendarEvent", calendarEvent);
              return "? unbekannt";
          }
        })(),
      )}
    </Typography>
  );

  if (!tooltip) {
    return status;
  }

  return (
    <Tooltip
      title={
        _isEventCanceled(calendarEvent) ? <FunctionField record={calendarEvent} render={_renderReason} /> : undefined
      }
      PopperProps={{ placement: "top-start" }}
    >
      {status}
    </Tooltip>
  );
}

function _isEventCanceled(event: AutovioCalendarEvent): boolean {
  return (
    event.status === "canceledByStudent" ||
    event.status === "canceledByInstructor" ||
    event.status === "declinedByStudent"
  );
}

const _renderReason = (record: AutovioCalendarEvent): string => {
  if (record.deletionReason) {
    return 'Grund: "' + record.deletionReason + '"';
  }
  if (record.student?.cancelReason) {
    return 'Grund: "' + record.student.cancelReason + '"';
  }
  return "Grund: Keine Angabe";
};
